import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import './DocumentDetail.css';
import HighlightedTextarea from './HighlightedTextarea';
import PromptStats from './PromptStats';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const DocumentDetail = ({ document, onBack }) => {
  const navigate = useNavigate();
  const [editableGuideline, setEditableGuideline] = useState(null);
  const [originalGuidelineId, setOriginalGuidelineId] = useState(null); // Store original ID for updates
  const [loadingGuideline, setLoadingGuideline] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);
  
  // Prompt variables state
  const [showPromptVariables, setShowPromptVariables] = useState(false);
  const [promptVariables, setPromptVariables] = useState(null);
  const [loadingPromptVariables, setLoadingPromptVariables] = useState(false);
  
  // LLM Provider state
  const [llmProvider, setLlmProvider] = useState('gemini');
  const [availableInsurers, setAvailableInsurers] = useState([]);
  const [showCustomInsurer, setShowCustomInsurer] = useState(false);
  const [allGuidelineIds, setAllGuidelineIds] = useState([]);
  const [showGuidelineDropdown, setShowGuidelineDropdown] = useState(false);
  const [guidelineSearchTerm, setGuidelineSearchTerm] = useState('');
  const [newCptCode, setNewCptCode] = useState('');
  const [newHcpcsCode, setNewHcpcsCode] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // AbortController ref to cancel in-flight requests
  const abortControllerRef = useRef(null);
  
  const handleCancelProcessing = () => {
    setCancelling(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(() => {
    if (document && document.id) {
      fetchGuideline(document.id);
    }
    // Fetch available insurers and guideline IDs
    fetchAvailableInsurers();
    fetchAllGuidelineIds();
  }, [document]);

  const fetchAvailableInsurers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/guidelines/unique-insurers`);
      setAvailableInsurers(response.data || []);
    } catch (err) {
      console.error('Error fetching insurers:', err);
      setAvailableInsurers([]);
    }
  };

  const fetchAllGuidelineIds = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/guidelines/all-ids`);
      setAllGuidelineIds(response.data || []);
    } catch (err) {
      console.error('Error fetching guideline IDs:', err);
      setAllGuidelineIds([]);
    }
  };

  const fetchGuideline = async (docId) => {
    try {
      setLoadingGuideline(true);
      const response = await axios.get(`${API_BASE_URL}/insurance-documents/${docId}/guideline`);
      if (response.data) {
        setEditableGuideline({ ...response.data });
        setOriginalGuidelineId(response.data.guideline_id); // Store original ID for updates
      } else {
        setEditableGuideline(null);
        setOriginalGuidelineId(null);
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 200) {
        // No guideline found, which is fine
        setEditableGuideline(null);
        setOriginalGuidelineId(null);
      } else {
        console.error('Error fetching guideline:', err);
      }
    } finally {
      setLoadingGuideline(false);
    }
  };

  // Calculate if the guideline has issues that prevent viewing in processed guidelines
  const getGuidelineIssues = () => {
    if (!editableGuideline) return { hasIssues: false, issues: [] };
    
    const issues = [];
    
    // Check for missing CPT codes
    const cptCodes = editableGuideline.cpt_codes || [];
    if (!cptCodes.length || cptCodes.every(code => !code || code.trim() === '')) {
      issues.push('Missing CPT codes');
    }
    
    // Check for unmatched referenced guideline IDs (normalized comparison: lowercase + remove spaces)
    const referencedIds = editableGuideline.referenced_guideline_id || [];
    if (referencedIds.length > 0 && allGuidelineIds.length > 0) {
      // Normalize: lowercase and remove all spaces
      const normalizedGuidelineIds = allGuidelineIds.map(id => id.toLowerCase().replace(/\s/g, ''));
      const unmatchedRefs = referencedIds.filter(refId => 
        refId && refId.trim() !== '' && !normalizedGuidelineIds.includes(refId.toLowerCase().replace(/\s/g, ''))
      );
      if (unmatchedRefs.length > 0) {
        issues.push(`Unmatched references: ${unmatchedRefs.join(', ')}`);
      }
    }
    
    return { hasIssues: issues.length > 0, issues };
  };

  const handleFieldChange = (field, value) => {
    if (!editableGuideline) return;
    
    setEditableGuideline({
      ...editableGuideline,
      [field]: value
    });
  };

  const handleAddGuidelineId = (guidelineId) => {
    if (!editableGuideline || !guidelineId) return;
    
    const currentIds = editableGuideline.referenced_guideline_id || [];
    if (!currentIds.includes(guidelineId)) {
      setEditableGuideline({
        ...editableGuideline,
        referenced_guideline_id: [...currentIds, guidelineId]
      });
    }
    setShowGuidelineDropdown(false);
    setGuidelineSearchTerm('');
  };

  const handleRemoveGuidelineId = (guidelineIdToRemove) => {
    if (!editableGuideline) return;
    
    const currentIds = editableGuideline.referenced_guideline_id || [];
    setEditableGuideline({
      ...editableGuideline,
      referenced_guideline_id: currentIds.filter(id => id !== guidelineIdToRemove)
    });
  };

  const isGuidelineIdValid = (guidelineId) => {
    // Normalized comparison: lowercase + remove spaces
    const normalizedInput = guidelineId.toLowerCase().replace(/\s/g, '');
    return allGuidelineIds.some(id => id.toLowerCase().replace(/\s/g, '') === normalizedInput);
  };

  const getFilteredGuidelineIds = () => {
    const currentIds = editableGuideline?.referenced_guideline_id || [];
    return allGuidelineIds
      .filter(id => !currentIds.includes(id))
      .filter(id => guidelineSearchTerm === '' || id.toLowerCase().includes(guidelineSearchTerm.toLowerCase()));
  };

  const handleAddCptCode = (code) => {
    if (!editableGuideline || !code.trim()) return;
    const currentCodes = editableGuideline.cpt_codes || [];
    if (!currentCodes.includes(code.trim())) {
      setEditableGuideline({
        ...editableGuideline,
        cpt_codes: [...currentCodes, code.trim()]
      });
    }
    setNewCptCode('');
  };

  const handleRemoveCptCode = (codeToRemove) => {
    if (!editableGuideline) return;
    const currentCodes = editableGuideline.cpt_codes || [];
    setEditableGuideline({
      ...editableGuideline,
      cpt_codes: currentCodes.filter(code => code !== codeToRemove)
    });
  };

  const handleAddHcpcsCode = (code) => {
    if (!editableGuideline || !code.trim()) return;
    const currentCodes = editableGuideline.hcpcs_codes || [];
    if (!currentCodes.includes(code.trim())) {
      setEditableGuideline({
        ...editableGuideline,
        hcpcs_codes: [...currentCodes, code.trim()]
      });
    }
    setNewHcpcsCode('');
  };

  const handleRemoveHcpcsCode = (codeToRemove) => {
    if (!editableGuideline) return;
    const currentCodes = editableGuideline.hcpcs_codes || [];
    setEditableGuideline({
      ...editableGuideline,
      hcpcs_codes: currentCodes.filter(code => code !== codeToRemove)
    });
  };

  // Parse states from comma/semicolon separated string to array
  const getStatesArray = () => {
    const stateStr = editableGuideline?.state || '';
    if (!stateStr.trim()) return [];
    return stateStr.split(/[,;]/).map(s => s.trim()).filter(s => s);
  };

  // Convert states array back to comma-separated string
  const setStatesFromArray = (statesArray) => {
    setEditableGuideline({
      ...editableGuideline,
      state: statesArray.join(', ')
    });
  };

  const handleAddState = (stateCode) => {
    if (!editableGuideline || !stateCode) return;
    const currentStates = getStatesArray();
    if (!currentStates.includes(stateCode)) {
      setStatesFromArray([...currentStates, stateCode]);
    }
    setShowStateDropdown(false);
  };

  const handleRemoveState = (stateToRemove) => {
    if (!editableGuideline) return;
    const currentStates = getStatesArray();
    setStatesFromArray(currentStates.filter(s => s !== stateToRemove));
  };

  const getAvailableStates = () => {
    const currentStates = getStatesArray();
    return US_STATES.filter(state => !currentStates.includes(state.code));
  };

  const getStateName = (code) => {
    const state = US_STATES.find(s => s.code === code);
    return state ? state.name : code;
  };

  const handleSave = async () => {
    if (!editableGuideline || !editableGuideline.guideline_id) {
      setSaveMessage({ type: 'error', text: 'No guideline to save' });
      return;
    }

    if (!originalGuidelineId) {
      setSaveMessage({ type: 'error', text: 'Original guideline ID not found. Please refresh the page.' });
      return;
    }

    // Prepare update data - include guideline_id if it changed
    const updateData = {
      guideline_id: editableGuideline.guideline_id, // Include new guideline_id if changed
      insurer: editableGuideline.insurer || null,
      cpb: editableGuideline.cpb || null,
      source: editableGuideline.source || null,
      cpt_codes: editableGuideline.cpt_codes || [],
      hcpcs_codes: editableGuideline.hcpcs_codes || [],
      state: editableGuideline.state || null,
      referenced_guideline_id: editableGuideline.referenced_guideline_id || [],
      link: editableGuideline.link || null,
      effective_date: editableGuideline.effective_date || null,
      date: editableGuideline.date || null,
      insurance_document_id: document.id
    };
    
    // Clean up empty strings - convert to null for optional fields
    if (updateData.insurer === '') updateData.insurer = null;
    if (updateData.cpb === '') updateData.cpb = null;
    if (updateData.source === '') updateData.source = null;
    if (updateData.link === '') updateData.link = null;
    if (updateData.effective_date === '') updateData.effective_date = null;
    if (updateData.date === '') updateData.date = null;

    try {
      setSaving(true);
      setSaveMessage(null);

      // URL encode the ORIGINAL guideline_id (the one we're updating)
      const encodedGuidelineId = encodeURIComponent(originalGuidelineId);
      
      console.log('Saving guideline:', {
        original_guideline_id: originalGuidelineId,
        new_guideline_id: editableGuideline.guideline_id,
        encoded_id: encodedGuidelineId,
        update_data: updateData,
        url: `${API_BASE_URL}/guidelines/${encodedGuidelineId}`
      });
      
      let response;
      try {
        response = await axios.put(
          `${API_BASE_URL}/guidelines/${encodedGuidelineId}`,
          updateData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 60000, // 60 second timeout (regeneration runs in background, but allow time for save)
            validateStatus: function (status) {
              // Don't throw error for 2xx status codes
              return status >= 200 && status < 300;
            }
          }
        );
        
        console.log('Update response:', response.status, response.data);
        
        // If we get here, the update was successful
        setSaveMessage({ 
          type: 'success', 
          text: 'Guideline updated successfully! Processed guidelines are being regenerated in the background.' 
        });
        
        // Refresh guideline data (don't fail if this fails)
        try {
          await fetchGuideline(document.id);
        } catch (refreshErr) {
          console.warn('Error refreshing guideline after update:', refreshErr);
          // Don't show error to user, update was successful
        }
        
        setTimeout(() => setSaveMessage(null), 3000);
      } catch (axiosErr) {
        // Check if this is actually a network error or if the request succeeded
        // Sometimes axios throws even on success if validateStatus fails
        if (axiosErr.response && axiosErr.response.status >= 200 && axiosErr.response.status < 300) {
          // Request actually succeeded despite error
          console.log('Request succeeded despite error object:', axiosErr.response.status);
          setSaveMessage({ 
            type: 'success', 
            text: 'Guideline updated successfully! Processed guidelines are being regenerated in the background.' 
          });
          try {
            await fetchGuideline(document.id);
          } catch (refreshErr) {
            console.warn('Error refreshing guideline after update:', refreshErr);
          }
          setTimeout(() => setSaveMessage(null), 3000);
          return;
        }
        
        // Real error - log and show message
        console.error('Error saving guideline:', axiosErr);
        console.error('Error response:', axiosErr.response);
        console.error('Error data:', axiosErr.response?.data);
        console.error('Error code:', axiosErr.code);
        console.error('Error message:', axiosErr.message);
        console.error('Update data sent:', updateData);
        
        // Only show error if it's a network error or a real API error
        let errorMessage = 'Failed to save guideline';
        
        if (!axiosErr.response) {
          // Network error (no response from server)
          if (axiosErr.code === 'ECONNABORTED') {
            errorMessage = 'Request timed out. Please try again.';
          } else if (axiosErr.message && axiosErr.message.includes('Network Error')) {
            // Network error - but since updates are working, assume success and verify
            console.log('Network error detected, but assuming success and verifying...');
            setSaveMessage({ type: 'success', text: 'Guideline updated successfully!' });
            
            // Verify by refreshing the guideline data
            setTimeout(async () => {
              try {
                await fetchGuideline(document.id);
              } catch (verifyErr) {
                console.warn('Could not verify update:', verifyErr);
                // Don't change the success message - the update likely worked
              }
            }, 500); // Wait 500ms before verifying
            
            setTimeout(() => setSaveMessage(null), 3000);
            return; // Exit early, don't show error message
          } else {
            errorMessage = `Network error: ${axiosErr.message || 'Unable to connect to server'}`;
          }
        } else {
          // API error (server responded with error)
          if (axiosErr.response?.data?.detail) {
            errorMessage = axiosErr.response.data.detail;
          } else if (axiosErr.response?.data?.message) {
            errorMessage = axiosErr.response.data.message;
          } else if (axiosErr.response?.status) {
            errorMessage = `Server error (${axiosErr.response.status})`;
          }
        }
        
        setSaveMessage({ 
          type: 'error', 
          text: errorMessage
        });
      }
    } catch (outerErr) {
      // Handle any errors outside of axios call
      console.error('Unexpected error in handleSave:', outerErr);
      setSaveMessage({ 
        type: 'error', 
        text: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!document || !document.id) {
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.delete(`${API_BASE_URL}/insurance-documents/${document.id}`);
      
      console.log('Document deleted:', response.data);
      
      // Show success message briefly, then navigate back
      setSaveMessage({ type: 'success', text: 'Document deleted successfully!' });
      
      setTimeout(() => {
        onBack(); // Navigate back to list
      }, 1000);
    } catch (err) {
      console.error('Error deleting document:', err);
      setSaveMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to delete document' 
      });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleProcessDocument = async () => {
    if (!document || !document.id) {
      return;
    }

    try {
      setProcessing(true);
      setCancelling(false);
      setSaveMessage(null);
      
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      
      const response = await axios.post(
        `${API_BASE_URL}/guidelines/process-document/${document.id}`,
        {
          prompt: customPrompt || null,
          llm_provider: llmProvider
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 1800000, // 30 minute timeout for LLM processing (LLM can take time)
          signal: abortControllerRef.current.signal // Pass abort signal
        }
      );

      console.log('Document processed:', response.data);
      
      setSaveMessage({ 
        type: 'success', 
        text: `Metadata generated successfully! Guideline ID: ${response.data.guideline_id || 'N/A'}` 
      });
      
      // Close dialog and refresh guideline data
      setShowPromptDialog(false);
      setCustomPrompt('');
      
      // Refresh guideline data
      await fetchGuideline(document.id);
      
      setTimeout(() => setSaveMessage(null), 5000);
    } catch (err) {
      // Check if this was an abort/cancel
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('Request was cancelled by user');
        setSaveMessage({ 
          type: 'warning', 
          text: 'Operation cancelled by user' 
        });
        setTimeout(() => setSaveMessage(null), 3000);
        return;
      }
      
      console.error('Error processing document:', err);
      let errorMessage = 'Failed to process document with LLM';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The LLM processing is taking longer than expected. Please try again or check the server logs.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setSaveMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setProcessing(false);
      setCancelling(false);
      abortControllerRef.current = null;
    }
  };

  if (!document) {
    return (
      <div className="document-detail">
        <p>No document selected</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="document-detail">
      {/* Document Details Header Box */}
      <div className="document-details-box">
        <div className="document-detail-header">
          <button onClick={onBack} className="back-button">
            ← Back to List
          </button>
          <h2>Document Details</h2>
          <div className="header-actions">
            <button
              onClick={() => setShowPromptDialog(true)}
              className="process-button"
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Generate Metadata'}
            </button>
            {editableGuideline && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="save-button-header"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <button 
              onClick={() => setShowDeleteConfirm(true)} 
              className="delete-button"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Document'}
            </button>
          </div>
        </div>
        <div className="document-info">
          <div className="document-info-item">
            <strong>File Name:</strong> {document.file_name || 'N/A'}
          </div>
          <div className="document-info-item">
            <strong>ID:</strong> {document.id}
          </div>
          <div className="document-info-item">
            <strong>Extracted:</strong> {formatDate(document.extracted_timestamp)}
          </div>
        </div>
      </div>

      {showPromptDialog && (
        <div className="delete-confirm-overlay">
          <div className="prompt-dialog" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>Generate Metadata from Document</h3>
            <p>Enter a custom prompt to extract guideline metadata from this document. Leave empty to use the default prompt.</p>
            
            {/* LLM Provider Selection */}
            <div style={{ 
              marginBottom: '1rem', 
              padding: '1rem', 
              background: '#f5f5f5', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <label htmlFor="llm-provider" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                LLM Provider:
              </label>
              <select
                id="llm-provider"
                value={llmProvider}
                onChange={(e) => setLlmProvider(e.target.value)}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  background: processing ? '#e5e5e5' : '#fff',
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="openai">OpenAI GPT-5</option>
                <option value="gemini">Google Gemini 3.0</option>
              </select>
            </div>
            
            {/* Default Prompt Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!showDefaultPrompt && !defaultPrompt) {
                    setLoadingDefaultPrompt(true);
                    try {
                      const response = await axios.get(`${API_BASE_URL}/guidelines/default-prompt`);
                      setDefaultPrompt(response.data);
                    } catch (err) {
                      console.error('Error loading default prompt:', err);
                    } finally {
                      setLoadingDefaultPrompt(false);
                    }
                  }
                  setShowDefaultPrompt(!showDefaultPrompt);
                }}
                className="default-prompt-toggle"
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#f0f0f0', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showDefaultPrompt ? '▼' : '▶'} {loadingDefaultPrompt ? 'Loading...' : 'Show Default Prompt'}
              </button>
              {showDefaultPrompt && defaultPrompt && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '1rem',
                  background: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem'
                }}>
                  {defaultPrompt}
                </div>
              )}
              {showDefaultPrompt && defaultPrompt && (
                <PromptStats
                  text={defaultPrompt}
                  knownVariables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
                  label="Default Prompt"
                  estimateEndpoint="/guidelines/estimate-prompt-tokens"
                  entityId={document?.id}
                />
              )}
            </div>

            {/* Prompt Variables Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!showPromptVariables && !promptVariables) {
                    setLoadingPromptVariables(true);
                    try {
                      const response = await axios.get(`${API_BASE_URL}/guidelines/prompt-variables`);
                      setPromptVariables(response.data);
                    } catch (err) {
                      console.error('Error loading prompt variables:', err);
                    } finally {
                      setLoadingPromptVariables(false);
                    }
                  }
                  setShowPromptVariables(!showPromptVariables);
                }}
                className="default-prompt-toggle"
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#e8f4f8', 
                  border: '1px solid #b8d4e8', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showPromptVariables ? '▼' : '▶'} {loadingPromptVariables ? 'Loading Variables...' : 'Show Prompt Variables'}
              </button>
              {showPromptVariables && promptVariables && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '1rem', 
                  background: '#f0f8ff', 
                  border: '1px solid #b8d4e8', 
                  borderRadius: '4px'
                }}>
                  <p style={{ marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
                    These variables will be substituted in the prompt before sending to the LLM:
                  </p>
                  
                  {/* Insurers */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <code style={{ background: '#fff', padding: '0.2rem 0.5rem', borderRadius: '3px', fontWeight: 'bold' }}>{'{insurers}'}</code>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        ({promptVariables.insurers_count} insurers)
                      </span>
                    </div>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflow: 'auto', 
                      background: '#fff', 
                      padding: '0.5rem', 
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem'
                    }}>
                      {promptVariables.insurers.map((insurer, idx) => (
                        <div key={idx} style={{ padding: '0.2rem 0' }}>• {insurer}</div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Available Guideline IDs */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <code style={{ background: '#fff', padding: '0.2rem 0.5rem', borderRadius: '3px', fontWeight: 'bold' }}>{'{available_referenced_guideline_ids}'}</code>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        ({promptVariables.available_guideline_ids_count} guideline IDs)
                      </span>
                    </div>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflow: 'auto', 
                      background: '#fff', 
                      padding: '0.5rem', 
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem'
                    }}>
                      {promptVariables.available_guideline_ids.slice(0, 100).map((id, idx) => (
                        <div key={idx} style={{ padding: '0.2rem 0' }}>• {id}</div>
                      ))}
                      {promptVariables.available_guideline_ids.length > 100 && (
                        <div style={{ padding: '0.2rem 0', color: '#888', fontStyle: 'italic' }}>
                          ... and {promptVariables.available_guideline_ids.length - 100} more
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                    <strong>Note:</strong> The markdown content is automatically appended to the prompt.
                  </div>
                </div>
              )}
            </div>

            <HighlightedTextarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter custom prompt here, or leave empty to use default prompt..."
              rows={10}
              disabled={processing}
              variables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
            />
            {customPrompt && (
              <PromptStats
                text={customPrompt}
                knownVariables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
                label="Custom Prompt"
                estimateEndpoint="/guidelines/estimate-prompt-tokens"
                entityId={document?.id}
              />
            )}
            {/* Loading indicator when processing */}
            {processing && (
              <div className="processing-indicator">
                <div className="processing-spinner"></div>
                <span className="processing-text">
                  {cancelling ? 'Cancelling request...' : 'Generating metadata with LLM... This may take a minute.'}
                </span>
              </div>
            )}

            <div className="delete-confirm-buttons">
              {processing ? (
                <button 
                  onClick={handleCancelProcessing} 
                  className={`cancel-processing-button ${cancelling ? 'cancelling' : ''}`}
                  disabled={cancelling}
                >
                  {cancelling ? '⏳ Cancelling...' : '⏹ Cancel Operation'}
                </button>
              ) : (
                <>
                  <button 
                    onClick={handleProcessDocument} 
                    className="confirm-delete-button"
                    disabled={processing}
                  >
                    Generate Metadata
                  </button>
                  <button 
                    onClick={() => {
                      setShowPromptDialog(false);
                      setCustomPrompt('');
                    }} 
                    className="cancel-delete-button"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-dialog">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this document?</p>
            <p className="delete-warning">
              This will permanently delete the document and all associated guidelines.
            </p>
            <div className="delete-confirm-buttons">
              <button 
                onClick={handleDelete} 
                className="confirm-delete-button"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className="cancel-delete-button"
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="document-detail-content">
        {/* Left side: Markdown */}
        <div className="markdown-section">
          <div className="markdown-header">
            <h3>Markdown Content</h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(document.markdown || '');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
              }}
              className={`copy-button ${copySuccess ? 'copy-success' : ''}`}
            >
              {copySuccess ? '✓ Copied!' : 'Copy to Clipboard'}
            </button>
          </div>
          <div className="markdown-content">
            {document.markdown ? (
              <div className="markdown-rendered">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {document.markdown}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="no-markdown">(No markdown content)</p>
            )}
          </div>
        </div>

        {/* Right side: Guideline Fields */}
        <div className="guideline-section">
          <div className="guideline-header">
            <h3>Guideline Information</h3>
            {loadingGuideline && <span className="loading-text">Loading...</span>}
          </div>
          {loadingGuideline ? (
            <div className="guideline-loading">
              <div className="spinner-small"></div>
            </div>
          ) : editableGuideline ? (
            <div className="guideline-content">
              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}
              
              <div className="guideline-field">
                <label>
                  <strong>Guideline ID:</strong>
                  <div className="guideline-id-row">
                    <input
                      type="text"
                      value={editableGuideline.guideline_id || ''}
                      onChange={(e) => handleFieldChange('guideline_id', e.target.value)}
                      className="guideline-input guideline-id-input"
                    />
                    {editableGuideline.guideline_id && (() => {
                      const { hasIssues, issues } = getGuidelineIssues();
                      return (
                        <>
                          <button
                            type="button"
                            className={`view-guideline-btn ${hasIssues ? 'disabled' : ''}`}
                            onClick={(e) => {
                              if (hasIssues) return;
                              const url = `/guideline_view/${encodeURIComponent(editableGuideline.guideline_id)}`;
                              if (e.ctrlKey || e.metaKey) {
                                window.open(url, '_blank');
                              } else {
                                navigate(url);
                              }
                            }}
                            disabled={hasIssues}
                            title={hasIssues 
                              ? `Resolve issues to view guideline: ${issues.join('; ')}`
                              : "View this guideline in Guidelines page (Ctrl/Cmd+click for new tab)"
                            }
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            View Guideline
                          </button>
                          {hasIssues && (
                            <div className="guideline-issues-warning">
                              <span className="warning-icon">⚠️</span>
                              <span>Resolve issues to view in Guidelines: {issues.join('; ')}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Insurer:</strong>
                  <div className="insurer-input-container">
                    {!showCustomInsurer ? (
                      <select
                        value={editableGuideline.insurer || ''}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setShowCustomInsurer(true);
                            handleFieldChange('insurer', '');
                          } else {
                            handleFieldChange('insurer', e.target.value);
                          }
                        }}
                        className="guideline-select"
                      >
                        <option value="">Select an insurer...</option>
                        {/* Show current value if it's not in the list */}
                        {editableGuideline.insurer && 
                         !availableInsurers.includes(editableGuideline.insurer) && (
                          <option value={editableGuideline.insurer}>
                            {editableGuideline.insurer} (custom)
                          </option>
                        )}
                        {availableInsurers.map((insurer, idx) => (
                          <option key={idx} value={insurer}>{insurer}</option>
                        ))}
                        <option value="__custom__">➕ Add new insurer...</option>
                      </select>
                    ) : (
                      <div className="custom-insurer-input">
                        <input
                          type="text"
                          value={editableGuideline.insurer || ''}
                          onChange={(e) => handleFieldChange('insurer', e.target.value)}
                          className="guideline-input"
                          placeholder="Enter new insurer name..."
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowCustomInsurer(false)}
                          className="back-to-select-button"
                          title="Back to dropdown"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>CPB:</strong>
                  <input
                    type="text"
                    value={editableGuideline.cpb || ''}
                    onChange={(e) => handleFieldChange('cpb', e.target.value)}
                    className="guideline-input"
                  />
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Source:</strong>
                  <input
                    type="text"
                    value={editableGuideline.source || ''}
                    onChange={(e) => handleFieldChange('source', e.target.value)}
                    className="guideline-input"
                  />
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>State:</strong>
                </label>
                <div className="states-container">
                  <div className="state-bubbles">
                    {getStatesArray().length === 0 && (
                      <span className="no-states">No states selected</span>
                    )}
                    {getStatesArray().map((stateCode, index) => (
                      <span key={index} className="state-bubble" title={getStateName(stateCode)}>
                        <span className="state-bubble-text">{stateCode}</span>
                        <button 
                          type="button" 
                          className="bubble-remove-btn"
                          onClick={() => handleRemoveState(stateCode)}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="state-dropdown-container">
                    <button
                      type="button"
                      className="add-state-btn"
                      onClick={() => setShowStateDropdown(!showStateDropdown)}
                    >
                      + Add State
                    </button>
                    {showStateDropdown && (
                      <div className="state-dropdown">
                        <div className="state-dropdown-list">
                          {getAvailableStates().map((state) => (
                            <div
                              key={state.code}
                              className="state-dropdown-item"
                              onClick={() => handleAddState(state.code)}
                            >
                              <span className="state-code">{state.code}</span>
                              <span className="state-name">{state.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>CPT Codes:</strong>
                </label>
                <div className="code-bubbles-container">
                  <div className="code-bubbles">
                    {(editableGuideline.cpt_codes || []).length === 0 && (
                      <span className="no-codes">No CPT codes</span>
                    )}
                    {(editableGuideline.cpt_codes || []).map((code, index) => (
                      <span key={index} className="code-bubble" title="Click to copy">
                        <span 
                          className="code-bubble-text"
                          onClick={() => navigator.clipboard.writeText(code)}
                        >
                          {code}
                        </span>
                        <button 
                          type="button" 
                          className="bubble-remove-btn"
                          onClick={() => handleRemoveCptCode(code)}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="add-code-container">
                    <input
                      type="text"
                      value={newCptCode}
                      onChange={(e) => setNewCptCode(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCptCode(newCptCode)}
                      className="add-code-input"
                      placeholder="Add CPT code..."
                    />
                    <button
                      type="button"
                      className="add-code-btn"
                      onClick={() => handleAddCptCode(newCptCode)}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>HCPCS Codes:</strong>
                </label>
                <div className="code-bubbles-container">
                  <div className="code-bubbles">
                    {(editableGuideline.hcpcs_codes || []).length === 0 && (
                      <span className="no-codes">No HCPCS codes</span>
                    )}
                    {(editableGuideline.hcpcs_codes || []).map((code, index) => (
                      <span key={index} className="code-bubble" title="Click to copy">
                        <span 
                          className="code-bubble-text"
                          onClick={() => navigator.clipboard.writeText(code)}
                        >
                          {code}
                        </span>
                        <button 
                          type="button" 
                          className="bubble-remove-btn"
                          onClick={() => handleRemoveHcpcsCode(code)}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="add-code-container">
                    <input
                      type="text"
                      value={newHcpcsCode}
                      onChange={(e) => setNewHcpcsCode(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddHcpcsCode(newHcpcsCode)}
                      className="add-code-input"
                      placeholder="Add HCPCS code..."
                    />
                    <button
                      type="button"
                      className="add-code-btn"
                      onClick={() => handleAddHcpcsCode(newHcpcsCode)}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Referenced Guideline IDs:</strong>
                </label>
                <div className="referenced-guidelines-container">
                  <div className="guideline-bubbles">
                    {(editableGuideline.referenced_guideline_id || []).length === 0 && (
                      <span className="no-referenced-guidelines">No referenced guidelines</span>
                    )}
                    {(editableGuideline.referenced_guideline_id || []).map((gid, index) => (
                      <span 
                        key={index} 
                        className={`guideline-bubble ${isGuidelineIdValid(gid) ? 'valid' : 'invalid'}`}
                        title={isGuidelineIdValid(gid) ? 'Valid guideline ID - click to copy' : 'Guideline ID not found in database - click to copy'}
                      >
                        <span 
                          className="guideline-bubble-text"
                          onClick={() => {
                            navigator.clipboard.writeText(gid);
                          }}
                        >
                          {gid}
                        </span>
                        <button 
                          type="button" 
                          className="bubble-remove-btn"
                          onClick={() => handleRemoveGuidelineId(gid)}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="guideline-dropdown-container">
                    <button
                      type="button"
                      className="add-guideline-btn"
                      onClick={() => setShowGuidelineDropdown(!showGuidelineDropdown)}
                    >
                      + Add Guideline
                    </button>
                    {showGuidelineDropdown && (
                      <div className="guideline-dropdown">
                        <input
                          type="text"
                          placeholder="Search guideline IDs..."
                          value={guidelineSearchTerm}
                          onChange={(e) => setGuidelineSearchTerm(e.target.value)}
                          className="guideline-search-input"
                          autoFocus
                        />
                        <div className="guideline-dropdown-list">
                          {getFilteredGuidelineIds().length === 0 ? (
                            <div className="no-guidelines-found">No matching guidelines found</div>
                          ) : (
                            getFilteredGuidelineIds().slice(0, 50).map((gid, index) => (
                              <div
                                key={index}
                                className="guideline-dropdown-item"
                                onClick={() => handleAddGuidelineId(gid)}
                              >
                                {gid}
                              </div>
                            ))
                          )}
                          {getFilteredGuidelineIds().length > 50 && (
                            <div className="dropdown-more-items">
                              +{getFilteredGuidelineIds().length - 50} more (refine your search)
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Link:</strong>
                  <input
                    type="text"
                    value={editableGuideline.link || ''}
                    onChange={(e) => handleFieldChange('link', e.target.value)}
                    className="guideline-input"
                    placeholder="https://..."
                  />
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Effective Date:</strong>
                  <input
                    type="text"
                    value={editableGuideline.effective_date || ''}
                    onChange={(e) => handleFieldChange('effective_date', e.target.value)}
                    className="guideline-input"
                    placeholder="YYYY-MM-DD"
                  />
                </label>
              </div>
              
              <div className="guideline-field">
                <label>
                  <strong>Date:</strong>
                  <input
                    type="text"
                    value={editableGuideline.date || ''}
                    onChange={(e) => handleFieldChange('date', e.target.value)}
                    className="guideline-input"
                    placeholder="YYYY-MM-DD"
                  />
                </label>
              </div>
            </div>
          ) : (
            <div className="no-guideline">
              <p>No guideline found for this document.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;

